/**
 * In-memory keyring for end-to-end encryption. Unlocked scope keys — and the person's own
 * private key, once unlocked — live only in this tab's memory (never in storage); reloading the
 * page, locking, or being idle locks them again.
 */
import { create } from "zustand";
import {
  importScopeKey,
  keyProof,
  normalizeRecoveryKey,
  unwrapMemberSlot,
  unwrapScopeKey,
  unwrapUserPrivateKey,
  userKeyProof,
  type KeySlotData,
  type MemberSlotData,
  type ScopeSlotData,
} from "@brainlog/shared";

export interface ScopeInfo {
  id: string;
  workspaceId: string;
  scopeType: "workspace" | "note";
  targetId: string;
  slots: ScopeSlotData[];
  /** People the key was shared with through their personal key. */
  sharedWith?: string[];
}

/** The person's key pair as stored on the server (public key + wrapped private key). */
export interface PersonalKeyRecord {
  algorithm: string;
  publicKey: string;
  slots: KeySlotData[];
}

interface PersonalKey {
  userId: string;
  privateKey: CryptoKey;
  pkcs8: Uint8Array;
  publicKey: string;
  proof: string;
}

let personal: PersonalKey | null = null;

interface Unlocked {
  key: CryptoKey;
  raw: Uint8Array;
  proof: string;
}

const keys = new Map<string, Unlocked>();
const lockListeners = new Set<(scopeIds: string[]) => void>();

const AUTOLOCK_KEY = "brainlog-claude.autolock-minutes";
export const DEFAULT_AUTOLOCK_MINUTES = 30;

function readAutoLock() {
  try {
    const raw = localStorage.getItem(AUTOLOCK_KEY);
    if (raw === null) return DEFAULT_AUTOLOCK_MINUTES;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 ? v : DEFAULT_AUTOLOCK_MINUTES;
  } catch {
    return DEFAULT_AUTOLOCK_MINUTES;
  }
}

interface KeyringState {
  unlocked: string[];
  /** Whether the person's own private key is unlocked in this tab. */
  personalUnlocked: boolean;
  autoLockMinutes: number;
  setAutoLock(minutes: number): void;
}

export const useKeyring = create<KeyringState>((set) => ({
  unlocked: [],
  personalUnlocked: false,
  autoLockMinutes: readAutoLock(),
  setAutoLock(minutes) {
    try {
      localStorage.setItem(AUTOLOCK_KEY, String(minutes));
    } catch {
      /* ignore */
    }
    set({ autoLockMinutes: minutes });
  },
}));

const sync = () => useKeyring.setState({ unlocked: [...keys.keys()], personalUnlocked: !!personal });

export function getScopeKey(scopeId: string | null | undefined): Unlocked | null {
  return scopeId ? (keys.get(scopeId) ?? null) : null;
}

export function isUnlocked(scopeId: string | null | undefined) {
  return !!scopeId && keys.has(scopeId);
}

export function useIsUnlocked(scopeId: string | null | undefined) {
  return useKeyring((s) => !!scopeId && s.unlocked.includes(scopeId));
}

/** Keep a freshly generated key (the user just created it, so it is unlocked). */
export async function rememberScopeKey(scopeId: string, raw: Uint8Array) {
  keys.set(scopeId, { key: await importScopeKey(raw), raw, proof: await keyProof(raw, scopeId) });
  sync();
}

/** Unlock with a passphrase or the recovery key. Throws WrongKeyError for a wrong secret. */
export async function unlockScope(scope: ScopeInfo, secret: string, kind: "passphrase" | "recovery") {
  const slot = scope.slots.find((s): s is KeySlotData => s.kind === kind);
  if (!slot) throw new Error(kind === "recovery" ? "No recovery key was set up for this key." : "No passphrase is set for this key.");
  const value = kind === "recovery" ? normalizeRecoveryKey(secret) : secret;
  const { raw, key } = await unwrapScopeKey(slot, value, scope.id);
  keys.set(scope.id, { key, raw, proof: await keyProof(raw, scope.id) });
  sync();
}

// Personal key ---------------------------------------------------------------------

export const memberSlotFor = (scope: Pick<ScopeInfo, "slots">, userId: string | null | undefined) =>
  userId ? (scope.slots.find((s): s is MemberSlotData => s.kind === "member" && s.recipientUserId === userId.toLowerCase()) ?? null) : null;

export const getPersonalKey = () => personal;

export function usePersonalUnlocked() {
  return useKeyring((s) => s.personalUnlocked);
}

/** Keep a freshly created personal key (the person just chose its passphrase). */
export async function rememberPersonalKey(userId: string, pkcs8: Uint8Array, publicKey: string, privateKey: CryptoKey) {
  personal = { userId, pkcs8, publicKey, privateKey, proof: await userKeyProof(pkcs8, userId) };
  sync();
}

/** Unlock the personal key with its passphrase or recovery key. Throws WrongKeyError for a wrong secret. */
export async function unlockPersonalKey(record: PersonalKeyRecord, userId: string, secret: string, kind: "passphrase" | "recovery" = "passphrase") {
  const slot = record.slots.find((s) => s.kind === kind);
  if (!slot) throw new Error(kind === "recovery" ? "No recovery key was set up for your personal key." : "Your personal key has no passphrase.");
  const value = kind === "recovery" ? normalizeRecoveryKey(secret) : secret;
  const { pkcs8, privateKey } = await unwrapUserPrivateKey(slot, value, userId);
  await rememberPersonalKey(userId, pkcs8, record.publicKey, privateKey);
}

/** Open a key that was shared with this person, using their unlocked personal key. Returns false if not possible. */
export async function unlockWithPersonalKey(scope: ScopeInfo): Promise<boolean> {
  if (keys.has(scope.id)) return true;
  if (!personal) return false;
  const slot = memberSlotFor(scope, personal.userId);
  if (!slot) return false;
  const { raw, key } = await unwrapMemberSlot(slot, personal.privateKey, scope.id, personal.userId);
  keys.set(scope.id, { key, raw, proof: await keyProof(raw, scope.id) });
  sync();
  return true;
}

// Listeners run before the keys are dropped, so open notes can encrypt their offline copy first.
export function lockScope(scopeId: string) {
  if (!keys.has(scopeId)) return;
  for (const l of [...lockListeners]) l([scopeId]);
  keys.delete(scopeId);
  sync();
}

export function lockAll() {
  const ids = [...keys.keys()];
  if (!ids.length && !personal) return;
  if (ids.length) for (const l of [...lockListeners]) l(ids);
  keys.clear();
  personal = null;
  sync();
}

export function onLock(fn: (scopeIds: string[]) => void) {
  lockListeners.add(fn);
  return () => lockListeners.delete(fn);
}

// Auto-lock after inactivity ------------------------------------------------------
let lastActivity = Date.now();
if (typeof window !== "undefined") {
  const touch = () => (lastActivity = Date.now());
  for (const ev of ["keydown", "pointerdown", "wheel", "touchstart"]) window.addEventListener(ev, touch, { passive: true, capture: true });
  setInterval(() => {
    const minutes = useKeyring.getState().autoLockMinutes;
    if (minutes > 0 && (keys.size || personal) && Date.now() - lastActivity > minutes * 60_000) lockAll();
  }, 15_000);
}
